<template>
  <div class="wl-fhud">
    <div class="wl-fhud__row">
      <div class="wl-fhud__waypoint">
        <span class="wl-tri" /><b>{{ selectedIndex >= 0 ? selectedIndex + 1 : '–' }}</b>
        <button type="button" title="编辑航点" :disabled="selectedIndex < 0" @click="editor.rightPanelOpen.value = true; editor.activeActionIndex.value = -1"><EditOutlined /></button>
        <button type="button" title="删除航点" :disabled="selectedIndex < 0" @click="editor.removeSelected"><DeleteOutlined /></button>
      </div>

      <div class="wl-fhud__keys">
        <div><kbd :class="{ on: keys.q }">Q<i>↺</i></kbd><kbd :class="{ on: keys.w }">W<i>↑</i></kbd><kbd :class="{ on: keys.e }">E<i>↻</i></kbd></div>
        <div><kbd :class="{ on: keys.a }">A<i>←</i></kbd><kbd :class="{ on: keys.s }">S<i>↓</i></kbd><kbd :class="{ on: keys.d }">D<i>→</i></kbd></div>
      </div>

      <button type="button" class="wl-fhud__stat wl-fhud__speed" title="切换虚拟飞行器速度 (X)" @click="editor.toggleSpeedMode">
        <small>SPD<br />m/s</small><b>{{ speed }}</b>
      </button>

      <div class="wl-fhud__compass">
        <span class="wl-fhud__heading">{{ signedHeading(aircraft.heading).toFixed(1) }}°</span>
        <svg viewBox="0 0 120 120" width="100" height="100">
          <circle cx="60" cy="60" r="56" fill="#0d0e10cc" stroke="#4a4d55" />
          <g :transform="`rotate(${-aircraft.heading} 60 60)`">
            <g v-for="tick in 36" :key="tick" :transform="`rotate(${(tick - 1) * 10} 60 60)`">
              <line x1="60" y1="5" x2="60" :y2="(tick - 1) % 3 === 0 ? 13 : 9" stroke="#c9ccd2" stroke-width="1" />
            </g>
            <g v-for="mark in marks" :key="mark.angle" :transform="`rotate(${mark.angle} 60 60)`">
              <text x="60" y="24" text-anchor="middle" font-size="8" :fill="mark.label === 'N' ? '#ff4d4f' : '#fff'" :font-weight="mark.cardinal ? 700 : 400" :transform="`rotate(${-mark.angle} 60 24)`">{{ mark.label }}</text>
            </g>
            <line x1="60" y1="60" :x2="60 + 34 * Math.sin(poiBearing)" :y2="60 - 34 * Math.cos(poiBearing)" v-if="poiBearing !== null" stroke="#c77dff" stroke-width="2" stroke-dasharray="3 2" />
          </g>
          <path d="M60 30 L60 90" stroke="#2d8cf0" stroke-width="1" stroke-dasharray="2 3" />
          <path d="M60 40 L68 66 L60 60 L52 66 Z" fill="#2d8cf0" stroke="#fff" stroke-width="1" />
          <path :d="`M60 60 L${60 + 26 * Math.sin(gimbalRad)} ${60 - 26 * Math.cos(gimbalRad)}`" stroke="#19be6b" stroke-width="2" opacity="0.8" />
        </svg>
        <span class="wl-fhud__agl">AGL {{ agl.toFixed(1) }} m</span>
      </div>

      <div class="wl-fhud__stat wl-fhud__alt">
        <small>ALT<br />m</small><b>{{ aircraft.height.toFixed(0) }}</b>
        <em v-if="asl !== null">{{ asl.toFixed(1) }} ASL</em>
        <em v-else-if="absolute !== null">{{ absolute.toFixed(1) }} HAE</em>
      </div>

      <div class="wl-fhud__vertical">
        <kbd :class="{ on: keys.c }">C<i>▲</i></kbd>
        <div class="wl-fhud__gimbal" title="云台俯仰角 (I 抬头 / K 低头)">
          <div class="wl-fhud__gimbal-track">
            <span class="wl-fhud__gimbal-mark" :style="{ top: gimbalPercent + '%' }" />
          </div>
          <b>{{ Math.round(aircraft.gimbalPitch) }}°</b>
        </div>
        <kbd :class="{ on: keys.z }">Z<i>▼</i></kbd>
      </div>
    </div>

    <div class="wl-fhud__row wl-fhud__coords">
      <label>经度<input type="number" step="0.000001" :value="coordinate.longitude.toFixed(7)" :readonly="selectedIndex < 0" @change="setLongitude" /></label>
      <label>纬度<input type="number" step="0.000001" :value="coordinate.latitude.toFixed(7)" :readonly="selectedIndex < 0" @change="setLatitude" /></label>
      <span class="wl-fhud__mode">{{ speedLabel }} · {{ viewLabel }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons-vue'
import { AIRCRAFT_SPEED_MODES, absoluteHeight, bearing, signedHeading, toRadians } from '/@/utils/wayline-editor'
import { ellipsoidToMeanSeaLevel } from '/@/utils/geoid'
import { useWaylineEditor } from './editor-context'

const editor = useWaylineEditor()
const draft = editor.draft
const aircraft = editor.aircraft
const keys = computed(() => editor.pressedKeys.value)
const selectedIndex = computed(() => editor.selected.value.length === 1 ? draft.waypoint.waypoints.indexOf(editor.selected.value[0]) : -1)
const speed = computed(() => AIRCRAFT_SPEED_MODES[aircraft.speedMode].speed)
const speedLabel = computed(() => AIRCRAFT_SPEED_MODES[aircraft.speedMode].label)
const viewLabel = computed(() => ({ free: '自由视角', follow: '视角跟随', fpv: '第一视角' })[editor.viewMode.value])
const absolute = computed(() => absoluteHeight(draft, aircraft.height, editor.heightMode.value))
const asl = computed(() => absolute.value === null ? null : ellipsoidToMeanSeaLevel(aircraft.latitude, aircraft.longitude, absolute.value))
const agl = editor.aircraftAgl
const gimbalRad = computed(() => toRadians(aircraft.gimbalPitch + 90))
const pitchRange = computed<[number, number]>(() => editor.droneProfile.value?.gimbalPitchRange || [-90, 30])
const gimbalPercent = computed(() => (pitchRange.value[1] - aircraft.gimbalPitch) / (pitchRange.value[1] - pitchRange.value[0]) * 100)
const marks = [
  { angle: 0, label: 'N', cardinal: true }, { angle: 30, label: '3', cardinal: false }, { angle: 60, label: '6', cardinal: false },
  { angle: 90, label: 'E', cardinal: true }, { angle: 120, label: '12', cardinal: false }, { angle: 150, label: '15', cardinal: false },
  { angle: 180, label: 'S', cardinal: true }, { angle: 210, label: '21', cardinal: false }, { angle: 240, label: '24', cardinal: false },
  { angle: 270, label: 'W', cardinal: true }, { angle: 300, label: '30', cardinal: false }, { angle: 330, label: '33', cardinal: false },
]
const poiBearing = computed(() => {
  const target = editor.selected.value[0]
  const heading = target ? (target.useGlobalHeadingParam ? draft.waypoint.globalHeadingParam : target.headingParam) : draft.waypoint.globalHeadingParam
  if (heading.waypointHeadingMode !== 'towardPOI' || !heading.waypointPoiPoint) return null
  return toRadians(bearing(aircraft, { longitude: heading.waypointPoiPoint[0], latitude: heading.waypointPoiPoint[1] }))
})
const coordinate = computed(() => selectedIndex.value >= 0 ? editor.selected.value[0] : aircraft)

function setLongitude (event: Event) {
  const value = Number((event.target as HTMLInputElement).value)
  if (Number.isFinite(value)) editor.moveSelectedTo(value, coordinate.value.latitude)
}
function setLatitude (event: Event) {
  const value = Number((event.target as HTMLInputElement).value)
  if (Number.isFinite(value)) editor.moveSelectedTo(coordinate.value.longitude, value)
}
</script>

<style lang="scss">
.wl-fhud {
  display: flex; flex-direction: column; align-items: center; gap: 6px; color: #fff; text-shadow: 0 1px 2px #000;
  &__row { display: flex; align-items: center; gap: 8px; }
  &__waypoint { display: flex; align-items: center; gap: 6px; height: 28px; padding: 0 8px; border-radius: 4px; background: #151618cc; }
  &__waypoint b { font-size: 13px; }
  &__waypoint button { width: 22px; height: 22px; border: 0; border-radius: 3px; background: transparent; color: #fff; cursor: pointer; }
  &__waypoint button:hover:not(:disabled) { background: #ffffff1f; }
  &__waypoint button:disabled { color: #5c6068; cursor: not-allowed; }
  &__keys { display: flex; flex-direction: column; gap: 3px; }
  &__keys div { display: flex; gap: 3px; }
  kbd { display: inline-flex; flex-direction: column; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 4px; background: #151618cc; border: 1px solid #4a4d55; color: #fff; font-family: inherit; font-size: 12px; line-height: 1; }
  kbd i { font-style: normal; font-size: 8px; color: #9a9ea6; }
  kbd.on { background: #2d8cf0; border-color: #2d8cf0; }
  kbd.on i { color: #fff; }
  &__stat { display: flex; flex-direction: column; align-items: center; min-width: 54px; padding: 4px 6px; border: 0; border-radius: 4px; background: #151618cc; color: #fff; cursor: default; }
  &__stat small { font-size: 9px; line-height: 1.1; color: #9a9ea6; text-align: center; }
  &__stat b { font-size: 22px; font-weight: 600; line-height: 1.1; color: #19be6b; font-variant-numeric: tabular-nums; }
  &__stat em { font-style: normal; font-size: 10px; color: #c9ccd2; white-space: nowrap; }
  &__speed { cursor: pointer; }
  &__compass { position: relative; display: flex; flex-direction: column; align-items: center; }
  &__heading { position: absolute; top: -4px; font-size: 12px; color: #19be6b; font-variant-numeric: tabular-nums; }
  &__agl { position: absolute; bottom: -2px; font-size: 10px; color: #c9ccd2; }
  &__vertical { display: flex; flex-direction: column; align-items: center; gap: 3px; }
  &__gimbal { display: flex; align-items: center; gap: 4px; height: 56px; }
  &__gimbal-track { position: relative; width: 6px; height: 100%; border-radius: 3px; background: linear-gradient(#4a4d55, #19be6b); }
  &__gimbal-mark { position: absolute; left: -3px; width: 12px; height: 4px; margin-top: -2px; border-radius: 2px; background: #ff9f1a; }
  &__gimbal b { font-size: 11px; font-weight: 500; font-variant-numeric: tabular-nums; width: 30px; }
  &__coords { gap: 8px; }
  &__coords label { display: inline-flex; align-items: center; gap: 6px; height: 24px; padding: 0 4px 0 8px; border-radius: 4px; background: #151618cc; color: #c9ccd2; }
  &__coords input { width: 118px; height: 20px; border: 1px solid #35373c; border-radius: 3px; background: #0d0e10; color: #fff; padding: 0 6px; font-size: 12px; font-variant-numeric: tabular-nums; outline: none; }
  &__coords input:read-only { color: #9a9ea6; }
  &__coords input:focus { border-color: #2d8cf0; }
  &__mode { color: #9a9ea6; }
}
</style>
